import { useMemo, useState } from 'react'
import { pathFor } from '../routing.js'
import { DeploymentStatus } from './status.jsx'
import { OutsideHint } from './outside.jsx'
import { AwaitingHint } from './approvals.jsx'
import { ProtectionLock } from './lock.jsx'
import { formatAge, formatInstant } from '../format.js'
import { QrveyBadge } from './account-settings.jsx'
import { LifetimeCell } from './lifetime.jsx'
import { AccountLink } from './account-link.jsx'
import { SearchBackendLabel } from './search-backend.jsx'
import type { Fleet as FleetData, FleetInstance } from '@qim/shared'
import type { JSX } from 'react'

/**
 * Every instance, across every customer — the console's front door (IM-104).
 *
 * ## Why this is the landing screen
 *
 * The console used to open on a list of customer names. Nobody arrives wanting
 * that: they arrive because something is running, something failed, or someone
 * is waiting on a deploy, and all three were four levels down. This puts them
 * first and leaves the customer list one click away, where the per-customer work
 * — onboarding, registration, access — still belongs.
 *
 * ## Why it is safe on page load
 *
 * It reads this control plane's own records, derived from runs the same way the
 * per-customer screen already derives them. That is one request. IM-101's scan
 * of AWS itself is the expensive question and stays explicitly started.
 *
 * ## Why it is Qrvey's own instances only
 *
 * Since the secondary navigation the Fleet is the estate Qrvey runs for itself
 * — tenants of kind `qrvey` — and a customer's instances live on that
 * customer's screen. The API does the narrowing (`GET /fleet?kind=qrvey`), so
 * this screen is never sent a customer's instance at all. The ⌘K palette
 * deliberately keeps the unfiltered read, because a support engineer jumps to a
 * *customer's* instance from it — see `fetchFleet` and `App`.
 *
 * ## Why the counts are filters
 *
 * A number you cannot click is a number you then have to go and find. Each one
 * narrows the table below it rather than navigating somewhere else, so the
 * answer appears where the question was asked.
 */

/** What the strip counts, and what each one narrows the table to. */
type Lens = 'all' | 'in-progress' | 'apply-failed' | 'live' | 'awaiting-approval'

const LENSES: readonly { readonly lens: Lens; readonly label: string; readonly tone: string }[] = [
  { lens: 'in-progress', label: 'Running now', tone: 'is-live' },
  { lens: 'apply-failed', label: 'Apply failed', tone: 'is-bad' },
  { lens: 'live', label: 'Live', tone: 'is-ok' },
  /*
   * The staff queue (IM-102, IM-39). A pending request nobody sees is not a
   * control — it waits out its window, or somebody chases it by hand — so it
   * is counted on the front door beside what is running and what failed. A
   * lens, not a status: the instance is still whatever it was.
   */
  { lens: 'awaiting-approval', label: 'Awaiting approval', tone: 'is-warn' },
  { lens: 'all', label: 'Instances', tone: '' },
]

const matches = (instance: FleetInstance, lens: Lens): boolean =>
  lens === 'all'
    ? true
    : lens === 'awaiting-approval'
      ? instance.awaitingApproval !== undefined
      : instance.status === lens

/**
 * How long each instance has been live, and the column that sorts by it
 * (IM-129).
 *
 * Sortable because it is the column the question is *about*: "what has been up
 * longest" is not answerable by scanning a list ordered by what was last
 * touched, which is the opposite fact. An instance created for a demo in June
 * and never destroyed has a recent-looking last run — the `output` someone ran
 * while investigating — and reads as active work.
 *
 * **No verdict, here or anywhere.** The column says how long; it does not say
 * "orphan" or "stale" or offer to clean anything up. Every real customer's
 * production instance is long-lived, and the judgement about any particular one
 * belongs to whoever owns it. What makes an old instance *interesting* is the
 * account it is in being internal, which is IM-127, and even then the reading
 * stays with the reader.
 *
 * `undefined` sorts last in both directions: `never-applied` and `destroyed`
 * have no age, and letting "no answer" win the top of a descending sort would
 * put the rows with nothing to say above every row that has something.
 */
type Order = 'default' | 'oldest' | 'newest'

const byLiveSince = (
  instances: readonly FleetInstance[],
  order: Order,
): readonly FleetInstance[] => {
  if (order === 'default') return instances
  return [...instances].sort((a, b) => {
    if (a.liveSince === undefined && b.liveSince === undefined) return 0
    if (a.liveSince === undefined) return 1
    if (b.liveSince === undefined) return -1
    return order === 'oldest'
      ? a.liveSince < b.liveSince
        ? -1
        : 1
      : a.liveSince < b.liveSince
        ? 1
        : -1
  })
}

/** What the header says it will do next, so the control is not a guess. */
const NEXT_ORDER: Record<Order, Order> = {
  default: 'oldest',
  oldest: 'newest',
  newest: 'default',
}

const SORT_LABEL: Record<Order, string> = {
  default: 'Sort by how long each has been live, oldest first',
  oldest: 'Sorted oldest first. Sort newest first',
  newest: 'Sorted newest first. Return to the default order',
}

/** `aria-sort` takes the state of *this* column, which is not the next action. */
const ARIA_SORT: Record<Order, 'none' | 'ascending' | 'descending'> = {
  default: 'none',
  oldest: 'ascending',
  newest: 'descending',
}

export const Fleet = ({
  fleet,
  onOpen,
  role,
}: {
  readonly fleet: FleetData
  readonly onOpen: (path: string) => void
  /** Whether the account column links to onboarding (IM-181). */
  readonly role: string
}): JSX.Element => {
  const [lens, setLens] = useState<Lens>('all')
  const [order, setOrder] = useState<Order>('default')

  const counts = useMemo(() => {
    const tally: Record<Lens, number> = {
      all: 0,
      'in-progress': 0,
      'apply-failed': 0,
      live: 0,
      'awaiting-approval': 0,
    }
    for (const instance of fleet.instances) {
      tally.all += 1
      if (instance.status === 'in-progress') tally['in-progress'] += 1
      if (instance.status === 'apply-failed') tally['apply-failed'] += 1
      if (instance.status === 'live') tally.live += 1
      if (instance.awaitingApproval !== undefined) tally['awaiting-approval'] += 1
    }
    return tally
  }, [fleet.instances])

  const shown = byLiveSince(
    fleet.instances.filter((instance) => matches(instance, lens)),
    order,
  )
  const accounts = new Set(fleet.instances.map((i) => i.accountKey)).size
  const regions = new Set(fleet.instances.map((i) => i.region).filter(Boolean)).size

  return (
    <>
      <section className="panel">
        <h2>Fleet</h2>
        <p className="hint">
          Qrvey&rsquo;s own instances. A customer&rsquo;s are on that customer&rsquo;s screen, and
          &#8984;K reaches any of them.
        </p>
        <div className="strip">
          {LENSES.map((entry) => (
            <button
              key={entry.lens}
              type="button"
              // A filter, not a link: the answer belongs where the question was
              // asked. `aria-pressed` because this is a toggle, not navigation.
              className={`stat ${entry.tone} ${lens === entry.lens ? 'on' : ''}`.trim()}
              aria-pressed={lens === entry.lens}
              onClick={() => setLens(lens === entry.lens ? 'all' : entry.lens)}
            >
              <span className="n">{counts[entry.lens]}</span>
              <span className="k">{entry.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* A customer whose records could not be read is said out loud rather than
        silently missing. One unreadable partition is not a reason to imply the
        rest of the estate is all there is. */}
      {fleet.unreadable.length === 0 ? null : (
        <p className="failure">
          Could not read {fleet.unreadable.map((gap) => gap.tenantName).join(', ')} just now, so any
          instances they have are missing from this list.
        </p>
      )}

      <section className="panel">
        <h3>
          {lens === 'all' ? 'All instances' : 'Filtered'}{' '}
          <span className="muted">
            · {shown.length} of {fleet.instances.length} across {accounts} account
            {accounts === 1 ? '' : 's'} and {regions} region{regions === 1 ? '' : 's'}
          </span>
        </h3>

        {fleet.instances.length === 0 ? (
          <p role="status">
            Nothing has been deployed yet. An instance appears here once a run names one.
          </p>
        ) : shown.length === 0 ? (
          <p role="status">No instance is in that state right now.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Instance</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Account</th>
                  <th scope="col">Region</th>
                  <th scope="col">Version</th>
                  <th scope="col">Search</th>
                  <th scope="col">Status</th>
                  <th scope="col" aria-sort={ARIA_SORT[order]}>
                    <button
                      type="button"
                      className="linkish"
                      title={SORT_LABEL[order]}
                      onClick={() => {
                        setOrder(NEXT_ORDER[order])
                      }}
                    >
                      Live for
                    </button>
                  </th>
                  {/* What it was meant for, beside how long it has been here.
                    The pair is the point: an age is only interesting against
                    something somebody said (IM-134). */}
                  <th scope="col">Meant for</th>
                  <th scope="col">Last touched</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((instance) => {
                  const href = pathFor({
                    screen: 'deployment',
                    tenantId: instance.tenantId,
                    deploymentId: instance.deploymentId,
                  })
                  return (
                    <tr key={`${instance.tenantId}/${instance.deploymentId}`}>
                      <td>
                        <a
                          href={href}
                          onClick={(event) => {
                            if (
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.button !== 0
                            ) {
                              return
                            }
                            event.preventDefault()
                            onOpen(href)
                          }}
                        >
                          {instance.deploymentId}
                        </a>
                        {/* Beside the name, where every other screen puts it
                          too (IM-174). Kind from the owner's record. */}
                        <ProtectionLock mark={instance.protected} kind={instance.tenantKind} />
                      </td>
                      <td>
                        <a
                          href={pathFor({ screen: 'customer', tenantId: instance.tenantId })}
                          onClick={(event) => {
                            if (
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.button !== 0
                            ) {
                              return
                            }
                            event.preventDefault()
                            onOpen(pathFor({ screen: 'customer', tenantId: instance.tenantId }))
                          }}
                        >
                          {instance.tenantName}
                        </a>{' '}
                        {/* Qrvey's own, marked here because this is the one
                          list that spans customers — one of Qrvey's instances
                          among customers' with nothing to say so is what makes
                          a long-lived instance here unreadable. On the customer
                          since IM-149, whose kind it is; it sat on the account
                          while IM-127's flag lived there. */}
                        <QrveyBadge kind={instance.tenantKind} />
                      </td>
                      <td className="mono-cell">
                        {/* To the account's onboarding screen (IM-181): when an
                          instance here fails, the account is the next question. */}
                        <AccountLink
                          tenantId={instance.tenantId}
                          accountKey={instance.accountKey}
                          role={role}
                          onOpen={onOpen}
                        />
                      </td>
                      {/* Region is a column rather than a tab: since IM-91 an
                        account holds instances in several, and a tab hides the
                        one that is broken. Moved onto the cell it describes
                        while resolving the IM-127/IM-129 merge — it had been
                        sitting above the account cell. */}
                      <td className="mono-cell">{instance.region ?? '—'}</td>
                      <td>{instance.chartVersion ?? '—'}</td>
                      {/* Beside the version, because it is read the same way:
                        from the newest succeeded apply (IM-182). */}
                      <td>
                        <SearchBackendLabel search={instance.search} />
                      </td>
                      <td>
                        <DeploymentStatus status={instance.status} />{' '}
                        {/* Why a destroyed instance never ran a destroy here, or
                          that the scan cannot find a live one (IM-156). */}
                        <OutsideHint deployment={instance} />
                        <AwaitingHint deployment={instance} />
                      </td>
                      {/* How long, never whether that is too long — see the note
                        above `byLiveSince`. A dash where there is nothing live:
                        a zero would read as brand new and a blank cell as a
                        rendering fault. */}
                      <td>
                        {instance.liveSince === undefined ? (
                          '—'
                        ) : (
                          <time
                            dateTime={instance.liveSince}
                            title={formatInstant(instance.liveSince)}
                          >
                            {formatAge(instance.liveSince)}
                          </time>
                        )}
                      </td>
                      <td>
                        <LifetimeCell
                          {...(instance.lifetime === undefined
                            ? {}
                            : { lifetime: instance.lifetime })}
                        />
                      </td>
                      <td>
                        {instance.lastRun === undefined ? (
                          /* Adopted and never run here (IM-140). Saying when it
                             was brought in beats an empty cell, which reads as a
                             rendering fault, and beats a dash, which reads as
                             "nothing is known". */
                          <>
                            <span className="muted">No run here yet</span>
                            {instance.adopted === undefined ? null : (
                              <p className="muted">
                                brought in by {instance.adopted.by} on{' '}
                                <time dateTime={instance.adopted.at}>
                                  {formatInstant(instance.adopted.at)}
                                </time>
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <time dateTime={instance.lastRun.requestedAt}>
                              {formatInstant(instance.lastRun.requestedAt)}
                            </time>
                            <p className="muted">
                              {instance.lastRun.command} by {instance.lastRun.requestedBy}
                            </p>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
